import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag } from '../../lib/lucide-proxy';
import { supabase } from '../../config/supabase';
import SEO from '../../components/seo/SEO';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import BlogInteraction from '../../components/blog/BlogInteraction'; // Added import

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*, category:blog_categories(name)')
          .eq('slug', slug)
          .single();

        if (error) throw error;
        setPost(data);
      } catch (error) {
        console.error('Error fetching post:', error);
        navigate('/blog'); // Redirect if not found
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>;
  }

  if (!post) return null;

  return (
    <>
      {/* SEO Injection */}
      <SEO 
        title={post.title} 
        description={post.excerpt} 
        image={post.featured_image} 
        type="article"
        publishedTime={post.created_at}
        author={post.author}
        section={post.category?.name}
        keywords={post.tags?.join(', ')}
      />

      <div className="bg-white min-h-screen pb-20">
        {/* Hero Image Standardization: Fixed height with object-cover */}
        <div className="h-[50vh] md:h-[60vh] w-full relative bg-dark-900">
          <img 
            src={post.featured_image || 'https://images.unsplash.com/photo-1608270586620-248524c67de9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'} 
            alt={post.title} 
            className="w-full h-full object-cover opacity-80" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          <div className="absolute inset-0 flex items-end pb-12 justify-center">
            <div className="max-w-4xl mx-auto px-4 text-center text-white w-full">
              {post.category && (
                <span className="inline-block bg-primary-500 px-4 py-1 rounded-full text-sm font-semibold mb-6 shadow-lg">
                  {post.category.name}
                </span>
              )}
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight shadow-black drop-shadow-md">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm sm:text-base text-gray-200">
                <div className="flex items-center space-x-2 bg-white/10 px-3 py-1 rounded-lg backdrop-blur-sm">
                  <Calendar className="w-5 h-5" />
                  <span>{new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
                </div>
                {post.author && (
                  <div className="flex items-center space-x-2 bg-white/10 px-3 py-1 rounded-lg backdrop-blur-sm">
                    <User className="w-5 h-5" />
                    <span>{post.author}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/blog" className="inline-flex items-center text-primary-500 hover:text-primary-600 mb-8 font-medium group transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform" />
            Voltar para o Blog
          </Link>

          {/* Content Rendering with Typography Plugin Classes */}
          <div 
            className="
              prose prose-lg max-w-none 
              prose-headings:font-display prose-headings:font-bold prose-headings:text-dark-900
              prose-p:text-dark-600 prose-p:leading-relaxed
              prose-a:text-primary-500 hover:prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-dark-900 prose-strong:font-bold
              prose-ul:list-disc prose-ul:pl-6 prose-li:text-dark-600
              prose-ol:list-decimal prose-ol:pl-6
              prose-blockquote:border-l-4 prose-blockquote:border-primary-500 prose-blockquote:bg-cream-100 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:italic prose-blockquote:rounded-r-lg
              prose-img:rounded-xl prose-img:shadow-md prose-img:w-full
              prose-hr:border-gray-200
            "
            dangerouslySetInnerHTML={{ __html: post.content }} 
          />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-100">
              <h4 className="text-sm font-bold text-dark-900 uppercase tracking-wider mb-4">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center px-3 py-1 rounded-lg bg-gray-100 text-dark-600 text-sm hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-default">
                    <Tag className="w-3 h-3 mr-2" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Social Interactions Component (Comments & Likes) */}
          <BlogInteraction postId={post.id} />

        </div>
      </div>
    </>
  );
};

export default BlogPost;